import { useState, useEffect } from 'react';
import { Download, X, Share, Plus } from 'lucide-react';

const DISMISS_KEY = 'fiskda-install-dismissed';
const DISMISS_DAYS = 7;

// Er vi på iOS (iPhone/iPad)? Safari sender ikke beforeinstallprompt,
// så der må vi vise manuell veiledning i stedet.
function isIos() {
  const ua = window.navigator.userAgent.toLowerCase();
  const isAppleDevice = /iphone|ipad|ipod/.test(ua);
  // iPad på iPadOS 13+ later som den er Mac, sjekk touch i tillegg
  const isIpadOs = ua.includes('macintosh') && 'ontouchend' in document;
  return isAppleDevice || isIpadOs;
}

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function recentlyDismissed() {
  const dismissed = localStorage.getItem(DISMISS_KEY);
  if (!dismissed) return false;
  const dismissedAt = parseInt(dismissed, 10);
  return Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [iosMode, setIosMode] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    // iOS: ingen beforeinstallprompt — vis manuell veiledning etter en liten stund
    if (isIos()) {
      const timer = setTimeout(() => {
        setIosMode(true);
        setShowPrompt(true);
      }, 4000);
      return () => clearTimeout(timer);
    }

    // Android/desktop: vent på beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[160] install-prompt">
      <div className="bg-white border-2 border-[#3B4D3C] rounded-lg shadow-2xl p-4 max-w-md mx-auto">
        <div className="flex items-center gap-4">
          <div className="bg-[#3B4D3C] p-2.5 rounded shrink-0">
            <Download size={20} className="text-[#F8F5F0]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-sans text-sm font-semibold text-[#3B4D3C]">
              Installer FiskDa
            </p>
            <p className="font-sans text-xs text-[#2D3436]/60">
              Legg til på hjemskjermen for raskere tilgang
            </p>
          </div>
          {!iosMode && (
            <button
              onClick={handleInstall}
              className="px-4 py-2 bg-[#3B4D3C] text-[#F8F5F0] rounded font-sans text-xs font-semibold shrink-0 active:scale-95 transition-transform"
            >
              Installer
            </button>
          )}
          <button
            onClick={handleDismiss}
            className="p-1 text-[#2D3436]/40 hover:text-[#2D3436] shrink-0 transition-colors"
            aria-label="Lukk"
          >
            <X size={18} />
          </button>
        </div>

        {/* iOS-veiledning — Safari kan ikke installere automatisk */}
        {iosMode && (
          <div className="mt-3 pt-3 border-t border-[#D1CCC0] font-sans text-xs text-[#2D3436]/80 flex flex-wrap items-center gap-1.5 leading-relaxed">
            <span>Trykk på</span>
            <Share size={15} className="inline text-[#3B4D3C]" />
            <span className="font-semibold">Del</span>
            <span>og deretter</span>
            <span className="inline-flex items-center gap-1 font-semibold">
              <Plus size={15} className="inline text-[#3B4D3C]" />
              «Legg til på Hjem-skjerm»
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
